// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
const _ = require('lodash')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common component imports
const SearchForm = require('client-v2/common/components/search-form')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { registerComponentEventHandlers } = require('client-v2/common/components/utils')
// Client V2 common container imports
const AppNav = require('client-v2/common/containers/app-nav/component-smart')
// Client V2 common state imports
const SelfUserRecord = require('client-v2/common/state/user/record')
// Client V2 common utils imports
const { mediaQueries } = require('client-v2/common/utils/responsive')
// Convenience; in lieu of JSX transpiling...
const { createElement: tag } = React

const SCROLL_THROTTLE_FREQUENCY = 100 // ms (~6 frames)

const bem = BemEntity('AppHeader')

class AppHeader extends React.PureComponent {
  constructor(props) {
    super(props)
    this.blockRef = React.createRef()
    this.searchFormRef = React.createRef()
    this.state = {
      isScrolledDown: false,
      isScrolledHidden: false,
      isScrollingUp: false,
      isSearchHidden: props.initialSearchHidden,
      scrollY: 0
    }
    // Bind event listeners to the component context
    registerComponentEventHandlers(this)
    this.onMediaQueryChange = this.onMediaQueryChange.bind(this)
    this.hideSearch = this.hideSearch.bind(this)
    this.toggleSearch = this.toggleSearch.bind(this)
    this.updateSearchFocus = this.updateSearchFocus.bind(this)
    // Throttle scroll listener to improve performance
    this.onScroll = _.throttle(this.onScroll.bind(this), SCROLL_THROTTLE_FREQUENCY)
  }

  // Component lifecycle

  componentDidMount() {
    if (typeof window === 'object') {
      window.addEventListener('scroll', this.onScroll)
      if (typeof matchMedia === 'function') {
        this.mediaQuery = matchMedia(mediaQueries['small-and-down'])
        this.mediaQuery.addListener(this.onMediaQueryChange)
      }
      this.updateScrollState()
    }
  }

  componentWillUnmount() {
    if (typeof window === 'object') {
      window.removeEventListener('scroll', this.onScroll)
      if (this.mediaQuery) {
        this.mediaQuery.removeListener(this.onMediaQueryChange)
        delete this.mediaQuery
      }
    }
  }

  // Scroll handling

  onMediaQueryChange() {
    this.updateScrollState()
  }

  onScroll() {
    const scrollY = typeof window === 'object' ? window.pageYOffset : 0
    if (scrollY !== this.state.scrollY) {
      const isScrollingUp = scrollY < this.state.scrollY
      this.updateScrollState({ isScrollingUp, scrollY })
    }
  }

  updateScrollState({ isScrollingUp, scrollY } = this.state) {
    const blockEl = this.blockRef.current
    if (blockEl) {
      const { height } = blockEl.getBoundingClientRect()
      const isSmallMedia = this.mediaQuery && this.mediaQuery.matches
      const isScrolledDown = scrollY > height
      const isScrolledHidden = isSmallMedia && isScrolledDown && !isScrollingUp
      this.setState({ isScrolledHidden, isScrolledDown, isScrollingUp, scrollY })
    }
  }

  // Search handling

  hideSearch() {
    this.setState({
      isSearchHidden: true
    }, this.updateSearchFocus)
  }

  toggleSearch() {
    this.setState({
      isSearchHidden: !this.state.isSearchHidden
    }, this.updateSearchFocus)
  }

  updateSearchFocus() {
    const searchForm = this.searchFormRef.current
    if (searchForm) {
      if (this.state.isSearchHidden) {
        searchForm.blur()
      } else {
        searchForm.focus()
      }
    }
  }

  // Render methods

  renderAuth() {
    if (this.props.user) {
      return null
    }
    return (
      tag('div', {
        className: bem.element('auth-container')
      },
        tag('button', {
          className: bem.element('auth-button', 'signup'),
          onClick: this.onSignupClick
        },
          `Join Mix`
        ),
        tag('button', {
          className: bem.element('auth-button', 'login'),
          onClick: this.onLoginClick
        },
          `Sign in`
        )
      )
    )
  }

  renderButtons() {
    return (
      tag('div', {
        className: bem.element('button-container')
      },
        tag('button', {
          className: bem.element('button', 'search'),
          onClick: this.toggleSearch
        },
          tag('span', { className: 'mdi-action-search' })
        ),
        this.props.user &&
          tag(UserAvatar, {
            className: bem.element('button', 'profile'),
            user: this.props.user,
            href: true,
            visible: true,
            onClick: this.onProfileClick
          }),
        this.props.user &&
          tag('button', {
            className: bem.element('button', 'add'),
            onClick: this.onAddClick
          },
            tag('span', { className: 'mdi-content-add' })
          )
      )
    )
  }

  renderLogo() {
    return (
      tag('a', {
        className: bem.element('logo-container'),
        href: '/'
      },
        tag('span', {
          className: bem.element('logo-icon')
        }),
        tag('span', {
          className: bem.element('logo-text')
        })
      )
    )
  }

  renderNav() {
    return (
      tag(AppNav, {
        className: bem.element('nav')
      })
    )
  }

  renderSearch() {
    return (
      tag('div', {
        className: bem.element('search-container')
      },
        tag(SearchForm, {
          ref: this.searchFormRef,
          className: bem.element('search-form'),
          initialValue: this.props.initialSearchValue,
          // Callbacks
          onNavigate: this.props.onSearchNavigate,
          onReset: this.hideSearch,
          onSubmit: this.props.onSearchSubmit
        })
      )
    )
  }

  renderSpacer() {
    return (
      tag('div', {
        className: bem.element('spacer')
      })
    )
  }

  render() {
    const { props, state } = this
    if (props.hidden) {
      return null
    }
    return (
      tag('div', {
        className: classNames(
          props.className,
          bem.block({
            'logged': props.user ? 'in' : 'out',
            'scrolled': state.isScrolledDown ? 'down' : 'up',
            'scrolled-hidden': state.isScrolledHidden,
            'searching': !state.isSearchHidden
          })
        ),
        ref: this.blockRef
      },
        tag('div', {
          className: bem.element('leading-container')
        },
          this.renderLogo(),
          this.renderSpacer(),
          this.renderSearch(),
          this.renderNav(),
          this.renderButtons()
        ),
        this.renderAuth()
      )
    )
  }
}

AppHeader.propTypes = {
  // Values
  className: PropTypes.string,
  hidden: PropTypes.bool,
  initialSearchHidden: PropTypes.bool,
  initialSearchValue: PropTypes.string,
  user: PropTypes.shape(SelfUserRecord.propTypes),
  // Callbacks
  onSearchNavigate: PropTypes.func,
  onSearchSubmit: PropTypes.func
}

// Event Handler Callbacks
AppHeader.eventHandlerKeys = [
  'onAddClick',
  'onLoginClick',
  'onProfileClick',
  'onSignupClick'
]
AppHeader.eventHandlerKeys.forEach(key => {
  AppHeader.propTypes[key] = PropTypes.func
})

AppHeader.defaultProps = {
  initialSearchHidden: true
}

module.exports = AppHeader
